import React, { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  Wallet,
  ArrowUpRight,
  ListChecks,
  ShieldCheck,
  Users,
  Menu,
  X,
  Settings2,
  ScrollText,
  Briefcase,
} from "lucide-react";
import { useUser } from "../context/UserContext";
import { Switch } from "../components/ui/switch";
import { Toaster } from "../components/ui/sonner";

const customerNav = [
  { to: "/", label: "Wallet", icon: Wallet, end: true },
  { to: "/withdraw", label: "Withdraw", icon: ArrowUpRight },
  { to: "/activity", label: "Activity", icon: ScrollText },
  { to: "/payouts", label: "Payouts", icon: ListChecks },
];

const adminNav = [
  { to: "/admin", label: "Admin", icon: ShieldCheck, end: true },
  { to: "/admin/payouts", label: "Approval Queue", icon: ListChecks },
  { to: "/admin/finance", label: "Finance", icon: Briefcase },
];

const PocBadge = () => (
  <div
    data-testid="poc-badge"
    className="w-full bg-slate-900 text-white text-[11px] sm:text-xs font-medium py-1.5 text-center tracking-wide sticky top-0 z-40"
  >
    POC — Sandbox Rail — No Real Funds Move.
  </div>
);

const RoleSwitcher = () => {
  const { users, activeUserId, switchUser } = useUser();
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3">
      <div className="flex items-center gap-2 px-1 pb-2">
        <Users className="h-4 w-4 text-slate-500" />
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          View as
        </span>
      </div>
      <div className="flex flex-col gap-1">
        {users.map((u) => {
          const active = u.id === activeUserId;
          return (
            <button
              key={u.id}
              data-testid={`role-switch-${u.role}-${u.name.split(" ")[0].toLowerCase()}`}
              onClick={() => switchUser(u.id)}
              className={`text-left flex items-center justify-between rounded-xl px-3 py-2 text-sm transition-colors ${
                active
                  ? "bg-teal-700 text-white"
                  : "hover:bg-slate-50 text-slate-700"
              }`}
            >
              <span className="truncate">{u.name}</span>
              <span
                className={`ml-2 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                  active
                    ? "bg-white/20 text-white"
                    : u.role === "admin"
                    ? "bg-orange-100 text-orange-700"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                {u.role}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const DemoControls = () => {
  const { demoMode, setDemoMode } = useUser();
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3">
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <Settings2 className="h-4 w-4 text-slate-500" />
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Demo controls
          </span>
        </div>
        <Switch
          data-testid="demo-controls-toggle"
          checked={demoMode}
          onCheckedChange={setDemoMode}
        />
      </div>
      <p className="mt-2 px-1 text-[11px] leading-snug text-slate-500">
        Show manual rail advance buttons in payout tracker.
      </p>
    </div>
  );
};

const SidebarInner = ({ onNavigate }) => {
  const { isAdmin, activeUser } = useUser();
  const nav = isAdmin ? adminNav : customerNav;
  return (
    <div className="flex h-full flex-col gap-4">
      <Link to="/" onClick={onNavigate} className="flex items-center gap-2 px-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-teal-700 text-white">
          <Wallet className="h-5 w-5" strokeWidth={2.25} />
        </div>
        <div className="leading-tight">
          <div className="font-display text-lg font-bold text-slate-900">Pay10x</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">
            × Thunes Rail
          </div>
        </div>
      </Link>

      <nav className="flex flex-col gap-1 px-1">
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={onNavigate}
            data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-teal-50 text-teal-800"
                  : "text-slate-600 hover:bg-slate-100"
              }`
            }
          >
            <n.icon className="h-4.5 w-4.5" strokeWidth={2} />
            {n.label}
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto flex flex-col gap-3">
        <RoleSwitcher />
        <DemoControls />
        {activeUser && (
          <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2 text-[11px] text-slate-500">
            Signed in as <span className="font-semibold text-slate-800">{activeUser.name}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  return (
    <div className="min-h-screen bg-slate-50">
      <PocBadge />
      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex sticky top-8 h-[calc(100vh-2rem)] w-72 shrink-0 flex-col border-r border-slate-200 bg-white p-5">
          <SidebarInner />
        </aside>

        {/* Mobile top bar */}
        <div className="flex flex-1 flex-col min-w-0">
          <div className="lg:hidden sticky top-8 z-30 flex items-center justify-between bg-white/90 backdrop-blur border-b border-slate-200 px-4 py-3">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-700 text-white">
                <Wallet className="h-4 w-4" strokeWidth={2.25} />
              </div>
              <span className="font-display font-bold text-slate-900">Pay10x</span>
            </div>
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setMobileOpen(true)}
              className="rounded-lg p-2 text-slate-700 hover:bg-slate-100"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>

          <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
            <div key={location.pathname} className="animate-fade-slide max-w-5xl mx-auto">
              <Outlet />
            </div>
          </main>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-slate-900/40"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 w-80 max-w-[85%] bg-white p-5 shadow-xl">
            <div className="mb-4 flex justify-end">
              <button
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-menu-close"
                className="rounded-lg p-2 hover:bg-slate-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <SidebarInner onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      <Toaster position="top-right" richColors />
    </div>
  );
}
